import _ from "lodash";
import { CheckboxGroupOption } from "src/components/Inputs/CheckboxGroup";
import { FILTER_DELIMITER, SearchAndFilterTypes } from "src/constants";
import { ColumnId } from "../formTables/constants";

// See all column keys and labels: https://docs.google.com/spreadsheets/d/1JaWyN5IC12AytEFczdlSeQ5-xvTVC5nToM_IfrtEPj8/edit?usp=sharing

export type ColumnGroup = {
  groupName: string;
  options: CheckboxGroupOption[];
  columnWidthMin?: number;
  searchable?: boolean;
  searchLabel?: string;
  searchPlaceholder?: string;
};

export const getAllColumnGroups = ({
  isLotteryEnabled,
  isRankingEnabled,
  isCurrentApplyingSchoolsEnabled,
  orgPath,
  questionIdsAndLabelsInOrder,
  formVerifications,
  tagGroups,
  disclaimerTitle,
}: {
  isLotteryEnabled: boolean;
  isRankingEnabled: boolean;
  orgPath: string | undefined;
  isCurrentApplyingSchoolsEnabled: boolean;
  questionIdsAndLabelsInOrder?: {
    id: uuid;
    label: string;
    isArchived: boolean;
  }[];
  formVerifications?: { id: uuid; label: string }[];
  tagGroups?: { id: uuid; label: string }[];
  disclaimerTitle?: string;
}): ColumnGroup[] => {
  return [
    {
      groupName: "Form information",
      options: getFormInfoOptions(isLotteryEnabled, isRankingEnabled, orgPath),
    },
    {
      groupName: "Student information",
      options: getStudentInfoOptions(isCurrentApplyingSchoolsEnabled),
    },
    {
      groupName: "Parent/Guardian information",
      options: guardianOptions,
    },
    ...(formVerifications && formVerifications.length > 0
      ? [
          {
            groupName: "Verifications",
            options: getDynamicOptions(formVerifications, "verification"),
          },
        ]
      : []),
    {
      groupName: "Tags",
      options: [
        { key: "tags-form", label: "Form tags" },
        ...(tagGroups ? getDynamicOptions(tagGroups, "tags") : []),
      ],
    },
    {
      groupName: "Questions",
      options: [
        ...(questionIdsAndLabelsInOrder
          ? getDynamicOptions(questionIdsAndLabelsInOrder, "question")
          : []),
        ...(disclaimerTitle ? [getDisclaimerOption(disclaimerTitle)] : []),
      ],
      columnWidthMin: 500,
      searchable: true,
      searchLabel: "Search for a question",
      searchPlaceholder: "Type a question title",
    },
  ];
};

export const getAllColumnInitialValues = (
  columnGroups: ColumnGroup[],
  isLotteryEnabled: boolean
) => {
  const initialValues = columnGroups
    .flatMap((group) =>
      group.options.map((option) => ({
        [option.key]: true,
      }))
    )
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
  if (!isLotteryEnabled) {
    initialValues["waitlist-position"] = false;
    initialValues["tiebreaker"] = false;
  }
  return initialValues;
};

export const getAllDefaultNonQuestionColumnKeys = ({
  isLotteryEnabled,
  isRankingEnabled,
  orgPath,
  isCurrentApplyingSchoolsEnabled,
  formVerifications,
  tagGroups,
}: {
  isLotteryEnabled: boolean;
  isRankingEnabled: boolean;
  orgPath: string | undefined;
  isCurrentApplyingSchoolsEnabled: boolean;
  formVerifications: { id: uuid; label: string }[];
  tagGroups: { id: uuid; label: string }[];
}) => {
  const allColumnGroups = getAllColumnGroups({
    isLotteryEnabled,
    isRankingEnabled,
    orgPath,
    isCurrentApplyingSchoolsEnabled,
    formVerifications,
    tagGroups,
  });
  const allColumnValues = getAllColumnInitialValues(
    allColumnGroups,
    isLotteryEnabled
  );
  return Object.keys(allColumnValues).filter((key) => allColumnValues[key]);
};

const LOTTERY_ONLY_TOOLTIP =
  "This value will be blank if Lottery & Offers is turned off on the form.";

const getFormInfoOptions = (
  isLotteryEnabled: boolean,
  isRankingEnabled: boolean,
  orgPath: string | undefined
): CheckboxGroupOption[] => {
  return [
    {
      key: "form-id",
      label: "Form ID",
    },
    { key: "form-status", label: "Form status" },
    { key: "form-status-date", label: "Form status date" },
    { key: "form-submission-date", label: "Form submission date" },
    { key: "form-sub-status", label: "Form sub-status" },
    { key: "form-sub-status-date", label: "Form sub-status date" },
    { key: "offer-expires-at", label: "Offer expiration date" },
    { key: "errors", label: "Errors" },
    { key: "program-id", label: "Program ID" },
    { key: "program-label", label: "Program label" },
    {
      key: "waitlist-position",
      label: "Waitlist position",
      ...(!isLotteryEnabled
        ? {
            tooltipContent: LOTTERY_ONLY_TOOLTIP,
          }
        : {}),
    },
    {
      key: "lottery-position",
      label: "Lottery position",
      ...(!isLotteryEnabled
        ? {
            tooltipContent: LOTTERY_ONLY_TOOLTIP,
          }
        : {}),
    },
    {
      key: "tiebreaker",
      label: "Tiebreaker",
      ...(!isLotteryEnabled
        ? {
            tooltipContent: LOTTERY_ONLY_TOOLTIP,
          }
        : {}),
    },
    ...(orgPath === "nolaps" // This is hardcoded for NOLA, as they requested to have the form school rank id as a unique row identifier.
      ? [{ key: "form-school-row-id", label: "Form school row ID" }]
      : []),
    ...(isRankingEnabled ? [{ key: "rank", label: "Rank" }] : []),
    { key: "eligibility-override", label: "Eligibility override" },
    { key: "override-reason", label: "Override reason" },
    { key: "school-id", label: "School ID" },
    { key: "school", label: "School" },
    { key: "school-reference-id", label: "School reference ID" },
    { key: "school-submission-date", label: "School submission date" },
    { key: "grade", label: "Grade" },
  ];
};

const getStudentInfoOptions = (
  isCurrentApplyingSchoolsEnabled: boolean
): CheckboxGroupOption[] => {
  return [
    {
      key: "student-id",
      label: "Student ID",
    },
    { key: "student-reference-id", label: "Student reference ID" },
    {
      key: "student-first-name",
      label: "Student first name",
    },
    {
      key: "student-middle-name",
      label: "Student middle name",
    },
    {
      key: "student-last-name",
      label: "Student last name",
    },
    { key: "student-address", label: "Student address" },
    { key: "student-date-of-birth", label: "Student date of birth" },
    ...(isCurrentApplyingSchoolsEnabled
      ? [
          { key: "attendance-status", label: "Attendance status" },
          { key: "attendance-school", label: "Attending school name" },
          { key: "attendance-school-id", label: "Attending school ID" },
          {
            key: "attendance-school-reference-id",
            label: "Attending school reference ID",
          },
        ]
      : []),
  ];
};

const guardianOptions = [
  {
    key: "guardian-id",
    label: "Parent/Guardian ID",
  },
  { key: "guardian-reference-id", label: "Parent/Guardian reference ID" },
  {
    key: "guardian-first-name",
    label: "Parent/Guardian first name",
  },
  {
    key: "guardian-middle-name",
    label: "Parent/Guardian middle name",
  },
  {
    key: "guardian-last-name",
    label: "Parent/Guardian last name",
  },
  { key: "guardian-address", label: "Parent/Guardian address" },
  { key: "guardian-email", label: "Parent/Guardian email" },
  { key: "guardian-phone", label: "Parent/Guardian phone" },
  { key: "guardian-language", label: "Parent/Guardian preferred language" },
];

const getDynamicOptions = (
  options: { id: uuid; label: string; isArchived?: boolean }[],
  prefix: string
): CheckboxGroupOption[] => {
  return options.map((option) => ({
    key: `${prefix}-${option.id}`,
    label: option.label,
    isArchived: option.isArchived ?? false,
  }));
};

const getDisclaimerOption = (disclaimerTitle: string): CheckboxGroupOption => {
  return {
    key: "disclaimer",
    label: disclaimerTitle,
  };
};

export const getExportColumnKeysFromSearchParams = (
  searchParams: URLSearchParams,
  verificationIds?: string[]
): string[] | undefined => {
  const selectedColumns = searchParams
    .get(SearchAndFilterTypes.VisibleColumns)
    ?.split(FILTER_DELIMITER);
  if (!selectedColumns) {
    return undefined;
  }
  return _.uniq(
    selectedColumns.flatMap((columnKey) =>
      mapFormListColumnKeyToExportColumnKey(columnKey, verificationIds)
    )
  );
};

const mapFormListColumnKeyToExportColumnKey = (
  key: string,
  verificationIds?: string[]
): string[] => {
  if (key.startsWith("disclaimer-")) {
    return ["disclaimer"];
  }
  switch (key) {
    case ColumnId.SchoolRank:
      return ["rank"];
    case ColumnId.Grade:
      return ["grade"];
    case ColumnId.Status:
      return [`form-${key}`];
    case ColumnId.StatusDate:
      return [`form-${key}`];
    case ColumnId.SubStatus:
      return [`form-${key}`, "offer-expires-at"];
    case ColumnId.SubStatusDate:
      return [`form-${key}`];
    case ColumnId.ID:
      return [`form-${key}`];
    case ColumnId.SubmittedDate:
      return ["form-submission-date"];
    case ColumnId.Program:
      return ["program-label"];
    case ColumnId.WaitlistPosition:
      return ["waitlist-position"];
    case ColumnId.SchoolSubmittedDate:
      return ["school-submission-date"];
    case ColumnId.Verifications:
      return verificationIds?.map((id) => `verification-${id}`) ?? [];
    case ColumnId.Tags:
      return ["tags-form"];
    case ColumnId.StudentId:
      return ["student-id", "student-reference-id"];
    case ColumnId.AttendingSchool:
      return ["attendance-school"];
    case ColumnId.AttendingSchoolId:
      return ["attendance-school-id"];
    case ColumnId.AttendingSchoolReferenceId:
      return ["attendance-school-reference-id"];
    case ColumnId.GuardianId:
      return ["guardian-id", "guardian-reference-id"];
    default:
      return [key];
  }
};

export const isQuestionColumnKey = (key: string): boolean => {
  return key.startsWith("question-");
};
